import { useTranslation } from 'react-i18next';
import { SocialSecurityNumberInput } from '@/components/forms/social-security-number-input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AssistantProfileUseForm } from './use-assistant-profile-form';

type Props = {
  form: AssistantProfileUseForm;
};

/**
 * Self-service profile form for medical assistants. Renders the personal and
 * address fields an assistant is allowed to edit. HR fields (contract type,
 * weekly hours, hire date) are intentionally excluded — they remain
 * admin-managed.
 *
 * @param props.form - the react-hook-form instance from {@link useAssistantProfileForm}
 */
const AssistantProfileForm = ({ form }: Props) => {
  const { t } = useTranslation();

  return (
    <Form {...form}>
      <form className="flex flex-col gap-4 py-4">
        <FormField
          control={form.control}
          name="firstname"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('assistant-profile-form.field.firstname')}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastname"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('assistant-profile-form.field.lastname')}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('assistant-profile-form.field.phone')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="dateOfBirth"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('assistant-profile-form.field.date-of-birth')}
              </FormLabel>
              <FormControl>
                <Input
                  type="date"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="birthPlace"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('assistant-profile-form.field.birth-place')}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="nationality"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('assistant-profile-form.field.nationality')}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="socialSecurityNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('assistant-profile-form.field.social-security-number')}
              </FormLabel>
              <FormControl>
                <SocialSecurityNumberInput
                  placeholder={t(
                    'assistant-profile-form.field.social-security-number-placeholder',
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="addressLine1"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('assistant-profile-form.field.address-line1')}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="addressLine2"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('assistant-profile-form.field.address-line2')}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="addressPostalCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('assistant-profile-form.field.address-postal-code')}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="addressCity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('assistant-profile-form.field.address-city')}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="addressState"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('assistant-profile-form.field.address-state')}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

export default AssistantProfileForm;
